import React from 'react'
import generatePicker, { PickerProps } from 'antd/es/date-picker/generatePicker'
import momentGenerateConfig from 'rc-picker/es/generate/moment'
import { useMediaQuery } from 'react-responsive'
import { Overwrite } from 'utility-types'
import cx from 'classnames'
import moment from 'moment-timezone'
import { getArrayValue } from '@commutifi-fe/utils'
import { screenSizes } from '@commutifi-fe/constants'
import './styles.scss'
import { spacing } from '../style/constants'

const AntdDatePicker = generatePicker<moment.Moment>(momentGenerateConfig)
export interface DatePickerComposition extends DatePickerType {
  MonthPicker: typeof AntdDatePicker.MonthPicker
  QuarterPicker: typeof AntdDatePicker.QuarterPicker
  RangePicker: typeof AntdDatePicker.RangePicker
  TimePicker: typeof AntdDatePicker.TimePicker
  WeekPicker: typeof AntdDatePicker.WeekPicker
  YearPicker: typeof AntdDatePicker.YearPicker
}

export type AppDatePickerProps = Overwrite<PickerProps<moment.Moment>, { prefix?: React.ReactNode }> & {
  popupClassName?: string | undefined
}

export const datePickerId = 'app-date-picker'

/**
 * This class is a wrap around [antd DatePicker](https://ant.design/components/list/) component extending some of
 * the properties to follow our styling guideline s.
 *
 * Please refer to antd documentation for more details.
 *
 */
const InternalDatePicker = React.forwardRef<any, AppDatePickerProps>(
  ({ className, style, prefix, popupClassName, popupStyle, value, ...props }: AppDatePickerProps, ref) => {
    const classNames = { className: cx('app-date-picker', className) }
    const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })
    // Since antd supports arrays for multiple data selector there is a tiny bug that doesn't accept string values for date so we need
    // to make sure it's converted to moment date objects
    const dateValue = React.useMemo(() => {
      if (!value) return null
      return getArrayValue(value).map((v) => (typeof v !== 'object' ? moment(v) : v.isValid() ? v : moment(v)))
    }, [value])

    return (
      <div className="app-date-picker-container">
        {prefix ? <span className="app-date-picker-prefix">{prefix}</span> : null}
        <AntdDatePicker
          ref={ref}
          style={{ ...(prefix && { paddingLeft: '32px' }), ...style }}
          data-testid={datePickerId}
          value={dateValue}
          // eslint-disable-next-line react/jsx-props-no-spreading -- Custom component wrapping antd component -> it's fine
          {...props}
          // eslint-disable-next-line react/jsx-props-no-spreading -- Custom component wrapping antd component -> it's fine
          {...classNames}
          popupClassName={cx(popupClassName, 'app-date-picker-dropdown')}
          // Fix dropdown panel going outside mobile screen.
          popupStyle={isXSmall ? { left: spacing.small, right: spacing.small, ...popupStyle } : popupStyle}
        />
      </div>
    )
  }
)
InternalDatePicker.displayName = 'DatePicker'

export type DatePickerType = typeof InternalDatePicker
export const DatePicker = InternalDatePicker as DatePickerComposition

DatePicker.MonthPicker = AntdDatePicker.MonthPicker
DatePicker.QuarterPicker = AntdDatePicker.QuarterPicker
DatePicker.RangePicker = AntdDatePicker.RangePicker
DatePicker.TimePicker = AntdDatePicker.TimePicker
DatePicker.WeekPicker = AntdDatePicker.WeekPicker
DatePicker.YearPicker = AntdDatePicker.YearPicker
export default DatePicker
